import type { IconName } from "@zapier/zinnia-icons";

export enum GlobalSearchEvent {
  Focus = "focus",
  Query = "query",
  SelectResult = "select_result",
  SearchResultsReturned = "search_results_returned",
  GuidepostCTAClick = "guidepost_cta_click",
  RecentlyUpdatedAssetClick = "recently_updated_asset_click",
  PromptClick = "prompt_click",
}

export interface GlobalSearchEmitPayload {
  event: GlobalSearchEvent;
  eventAction?: "click" | "view" | "edit";
  section?: string;
  text?: string;
  customData?: Record<string, unknown>;
}

export type GlobalSearchEmitFunction = (
  payload: GlobalSearchEmitPayload,
) => void;

export const RESOURCE_TYPE = {
  Agent: "Agent",
  App: "App",
  Blog: "Blog",
  Canvas: "Canvas",
  Chatbot: "Chatbot",
  CommunityQuestion: "CommunityQuestion",
  Folder: "Folder",
  Guidepost: "Guidepost",
  HelpArticle: "HelpArticle",
  Interface: "Interface",
  Table: "Table",
  Template: "Template",
  Zap: "Zap",
} as const;

export type ResourceType = (typeof RESOURCE_TYPE)[keyof typeof RESOURCE_TYPE];

export interface GuidepostCta {
  text?: string | null;
  iconUrl?: string | null;
  destinationUrl?: string | null;
}

export interface GuidepostEntry {
  id: string;
  title: string;
  url?: string | null;
  description?: string | null;
  category?: string | null;
  imgUrl?: string | null;
  primaryCta?: GuidepostCta | null;
  secondaryCta?: GuidepostCta | null;
  tertiaryCta?: GuidepostCta | null;
}

export interface SearchEntry {
  __typename?: string;
  id: string;
  title?: string | null;
  url?: string | null;
  image?: string | null;
  icons?: string[];
  services?: string[];
  updatedAt?: string | null;
}

export interface ResourceResult {
  subqueryId?: string | null;
  resourceName?: string | null;
  resourceNamePlural?: string | null;
  resourceHubUrl?: string | null;
  results?: SearchEntry[];
}

export interface GuidepostResourceResult {
  subqueryId?: string | null;
  resourceName?: string | null;
  resourceNamePlural?: string | null;
  resourceHubUrl?: string | null;
  results?: GuidepostEntry[];
}

export interface UniversalSearchResult {
  searchId: string;
  apps?: ResourceResult | null;
  templates?: ResourceResult | null;
  blogs?: ResourceResult | null;
  communityQuestions?: ResourceResult | null;
  helpArticles?: ResourceResult | null;
  guideposts?: GuidepostResourceResult | null;
  recentZapierAssets: SearchEntry[];
}

export type Index =
  | "guideposts"
  | "apps"
  | "templates"
  | "blogs"
  | "communityQuestions"
  | "helpArticles";

export interface SearchSection {
  key: Index | "assets";
  algoliaIndex?: string;
  icon?: IconName;
  iconColor?: string;
  data?:
    | ResourceResult
    | {
        subqueryId?: string | null;
        resourceName: string;
        resourceNamePlural: string;
        resourceHubUrl: string;
        results: SearchEntry[];
      }
    | null;
}

export interface PromptSuggestionsData {
  source: string | null;
  useCases: string[];
}

/**
 * Tracking callbacks that consumers can provide to wire their own analytics.
 */
export interface SearchDropdownCallbacks {
  onSearchQueryInputted?: (query: string) => void;
  onSearchResultsReturned?: (
    query: string,
    queryId: string,
    subqueryIds: string[],
  ) => void;
  onSearchResultClick?: (
    query: string,
    queryId: string,
    result: SearchEntry,
    section: string,
    position: number,
  ) => void;
  onAssetSearchResultClick?: (
    query: string,
    queryId: string,
    assetType: string | undefined,
    href: string,
    position: number,
  ) => void;
  onRecentlyUpdatedAssetClick?: (
    assetType: string | undefined,
    href: string,
  ) => void;
  onGuidepostInView?: (title: string, query: string) => void;
  onGuidepostCTAClick?: (
    title: string,
    query: string,
    cta: GuidepostCta,
    level: "primary" | "secondary" | "tertiary",
  ) => void;
  onPromptCategoriesInView?: () => void;
  onPromptCategoryClick?: (category: string, source?: string | null) => void;
  onPromptListInView?: (category: string, source?: string | null) => void;
  onPromptClick?: (prompt: string) => void;
}
